// ═══════════════════════════════════════════════════════════════════
//  modules/catalogue/CataloguePage.js
//  CataloguePage · Bareme · Recompenses
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { erreurDe, nomObjet } from "../ah/ah";
import { argent, METIERS, metierInfo, nombre } from "../erp/metiers";

// La page qui dit POURQUOI. Les classements montrent que cinq métiers sur
// huit ne bougent pas ; ici on sépare les deux causes possibles, qui
// n'appellent pas du tout la même correction :
//   - personne n'y joue          -> c'est un sujet d'animation
//   - rien ne le paie            -> c'est un barème à écrire
export function CataloguePage({ token }) {
  const [vue, setVue] = useState("bareme");
  const vues = [
    { id: "bareme", label: "⚖️ Barème" },
    { id: "recompenses", label: "🎁 Récompenses de palier" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ color: THEME.text, fontSize: 22, fontWeight: 800, margin: 0 }}>⚖️ Barème et récompenses</h1>
        <span style={{ color: THEME.textMuted, fontSize: 12 }}>lecture seule — la base fait foi, pas les fichiers YAML</span>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${THEME.border}` }}>
        {vues.map((v) => (
          <button key={v.id} onClick={() => setVue(v.id)}
            style={{
              background: vue === v.id ? THEME.panel : "transparent",
              border: `1px solid ${vue === v.id ? THEME.border : "transparent"}`,
              borderBottom: vue === v.id ? `1px solid ${THEME.panel}` : `1px solid ${THEME.border}`,
              color: vue === v.id ? THEME.text : THEME.textMuted,
              borderRadius: "6px 6px 0 0", padding: "8px 16px", cursor: "pointer",
              fontSize: 13, fontWeight: vue === v.id ? 600 : 400, marginBottom: -1,
            }}>{v.label}</button>
        ))}
      </div>

      <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: "0 8px 8px 8px", padding: 20, minHeight: 260 }}>
        {vue === "bareme" && <Bareme token={token} />}
        {vue === "recompenses" && <Recompenses token={token} />}
      </div>
    </div>
  );
}

const champ = {
  background: THEME.surface, border: `1px solid ${THEME.border}`,
  color: THEME.text, borderRadius: 6, padding: "7px 10px", fontSize: 12,
};

function Erreur({ texte }) {
  return (
    <div style={{ color: THEME.red, background: THEME.redDim, border: `1px solid ${THEME.red}`, borderRadius: 8, padding: "14px 18px", fontSize: 13 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>⛔ La route n'a pas répondu correctement</div>
      <code style={{ color: THEME.text, fontSize: 12, wordBreak: "break-word" }}>{texte}</code>
    </div>
  );
}

function Indisponible() {
  return (
    <div style={{ color: THEME.amber, background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
      ⏳ Les tables <code>oj_*</code> n'existent pas sur cette base. Elles sont créées au premier
      démarrage du plugin OrialisJobs.
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  LE BARÈME
// ═══════════════════════════════════════════════════════════════════════
function Bareme({ token }) {
  const [f, setF] = useState({ metier: "", action: "", q: "", actif: "", tri: "xp_desc" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v) q.set(k, v); });
      q.set("page", String(page));
      const res = await fetch(`${API_BASE}/api/catalogue/bareme?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, f, page]);
  useEffect(() => { load(); }, [load]);

  const changer = (k, v) => { setPage(1); setF((x) => ({ ...x, [k]: v })); };

  if (loading && !data) return <div style={{ color: THEME.textMuted }}>Chargement du barème...</div>;
  if (data?.unavailable) return <Indisponible />;
  const err = erreurDe(data);
  if (err) return <Erreur texte={err} />;

  // Le diagnostic. Quatre états, quatre corrections différentes.
  //
  // Le quatrième est celui auquel je ne pensais pas, et il est apparu en
  // fabriquant un jeu d'essai : AUCUNE ligne de barème, mais des joueurs
  // qui ont monté des niveaux. Ce n'est pas incohérent, c'est une trace :
  // des lignes ont été retirées ou désactivées APRÈS avoir payé. L'XP
  // acquise, elle, reste — rien ne la reprend. Un joueur peut donc être
  // niveau 17 dans un métier que plus rien ne fait progresser.
  const etat = (r) => {
    if (r.lignes === 0 && r.niveau_max > 0) return { cle: "retire", couleur: THEME.purple, fond: THEME.purpleDim, texte: `niveau ${r.niveau_max} atteint, mais plus rien ne le paie` };
    if (r.lignes === 0) return { cle: "muet", couleur: THEME.red, fond: THEME.redDim, texte: "aucune ligne ne le paie" };
    if (r.niveau_max === 0) return { cle: "jamais", couleur: THEME.amber, fond: THEME.amberDim, texte: r.joueurs === 0 ? "payé, mais personne n'y joue" : "payé, mais aucun niveau atteint" };
    return { cle: "ok", couleur: THEME.emerald, fond: THEME.emeraldDim, texte: `niveau max ${r.niveau_max}` };
  };
  const muets   = (data.resume || []).filter((r) => etat(r).cle === "muet");
  const retires = (data.resume || []).filter((r) => etat(r).cle === "retire");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── Le diagnostic, avant tout le reste ────────────────────── */}
      <div>
        <div style={{ color: THEME.textMuted, fontSize: 11, fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}>
          Qu'est-ce qui paie, et qu'est-ce qui monte
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(168px, 1fr))", gap: 8 }}>
          {(data.resume || []).map((r) => {
            const info = metierInfo(r.metier);
            const e = etat(r);
            return (
              <button key={r.metier} onClick={() => changer("metier", f.metier === r.metier ? "" : r.metier)}
                style={{
                  background: f.metier === r.metier ? THEME.panel : THEME.surface,
                  border: `1px solid ${f.metier === r.metier ? THEME.accent : e.couleur}`,
                  borderRadius: 8, padding: "9px 12px", cursor: "pointer", textAlign: "left",
                }}>
                <div style={{ color: THEME.text, fontSize: 12, fontWeight: 700, marginBottom: 3 }}>
                  {info.emoji} {info.label}
                </div>
                <div style={{ color: e.couleur, fontSize: 10, fontWeight: 600 }}>{e.texte}</div>
                <div style={{ color: THEME.textMuted, fontSize: 10, marginTop: 2 }}>
                  {r.lignes === 0 ? "0 ligne de barème" : (
                    <>{nombre(r.lignes)} ligne{r.lignes > 1 ? "s" : ""} · {nombre(r.joueurs)} joueur{r.joueurs > 1 ? "s" : ""}</>
                  )}
                </div>
                {r.lignes > 0 && (
                  <div style={{ color: THEME.textDim, fontSize: 10 }}>
                    XP {nombre(r.xp_mini, 2)} → {nombre(r.xp_maxi, 2)}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {muets.length > 0 && (
          <div style={{ color: THEME.red, background: THEME.redDim, border: `1px solid ${THEME.red}`, borderRadius: 8, padding: "10px 14px", fontSize: 12, marginTop: 10, lineHeight: 1.6 }}>
            <strong>{muets.length} métier{muets.length > 1 ? "s" : ""} n'{muets.length > 1 ? "ont" : "a"} aucune ligne de barème</strong>
            {" — "}{muets.map((m) => metierInfo(m.metier).label).join(", ")}.
            {" "}Ce n'est pas un problème d'animation : <strong>rien ne les paie</strong>, donc
            personne ne peut y monter d'un seul niveau, quoi qu'il fasse en jeu.
          </div>
        )}

        {retires.length > 0 && (
          <div style={{ color: THEME.purple, background: THEME.purpleDim, border: `1px solid ${THEME.purple}`, borderRadius: 8, padding: "10px 14px", fontSize: 12, marginTop: 10, lineHeight: 1.6 }}>
            <strong>{retires.length} métier{retires.length > 1 ? "s ont" : " a"} des joueurs mais plus aucune ligne de barème</strong>
            {" — "}{retires.map((m) => `${metierInfo(m.metier).label} (niveau ${m.niveau_max})`).join(", ")}.
            {" "}Des lignes ont été retirées ou désactivées <strong>après</strong> avoir payé.
            L'XP déjà acquise reste — rien ne la reprend — mais plus personne ne peut progresser.
            À vérifier avant l'ouverture : est-ce voulu, ou un import de classeur qui a effacé ?
          </div>
        )}

        <div style={{ color: THEME.textDim, fontSize: 10, marginTop: 8, lineHeight: 1.6 }}>
          En rouge : aucune ligne ne paie ce métier. En ambre : il est payé, mais personne n'y a
          atteint le niveau 1 — son barème n'a donc jamais été éprouvé. En violet : des joueurs y
          ont monté, mais plus rien ne le paie aujourd'hui.
          Clique une case pour filtrer le tableau.
        </div>
      </div>

      {/* ── Les filtres ───────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", borderTop: `1px solid ${THEME.border}`, paddingTop: 14 }}>
        <select style={champ} value={f.metier} onChange={(e) => changer("metier", e.target.value)}>
          <option value="">Tous les métiers</option>
          {METIERS.map((m) => <option key={m.key} value={m.key}>{m.emoji} {m.label}</option>)}
        </select>
        <select style={champ} value={f.action} onChange={(e) => changer("action", e.target.value)}>
          <option value="">Toutes les actions</option>
          {(data.actions || []).map((a) => <option key={a.action} value={a.action}>{a.action} ({a.total})</option>)}
        </select>
        <input style={{ ...champ, minWidth: 170 }} placeholder="Identifiant (DIAMOND_ORE…)"
          value={f.q} onChange={(e) => changer("q", e.target.value)} />
        <select style={champ} value={f.actif} onChange={(e) => changer("actif", e.target.value)}>
          <option value="">Actives et inactives</option>
          <option value="1">Actives seulement</option>
          <option value="0">Inactives seulement</option>
        </select>
        <select style={champ} value={f.tri} onChange={(e) => changer("tri", e.target.value)}>
          <option value="xp_desc">XP décroissante</option>
          <option value="xp">XP croissante</option>
          <option value="argent_desc">Argent décroissant</option>
          <option value="identifiant">Par identifiant</option>
          <option value="metier">Par métier</option>
        </select>
        <span style={{ marginLeft: "auto", color: THEME.textMuted, fontSize: 12 }}>
          {loading ? "…" : `${nombre(data.total)} ligne${data.total > 1 ? "s" : ""}`}
        </span>
      </div>

      {/* ── Le tableau ────────────────────────────────────────────── */}
      {data.lignes.length === 0 ? (
        <div style={{ color: THEME.textMuted, background: THEME.surface, borderRadius: 8, padding: 20, textAlign: "center", fontSize: 13 }}>
          Aucune ligne de barème ne correspond.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 720 }}>
            <thead>
              <tr style={{ color: THEME.textMuted, textAlign: "left" }}>
                {["Métier", "Action", "Ce qui paie", "XP", "Argent", "Niveaux", "État"].map((h) => (
                  <th key={h} style={{ padding: "7px 10px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", borderBottom: `1px solid ${THEME.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.lignes.map((l) => {
                const info = metierInfo(l.metier);
                return (
                  <tr key={l.id} style={{ borderBottom: `1px solid ${THEME.border}`, opacity: l.actif ? 1 : 0.5 }}>
                    <td style={{ padding: "7px 10px", color: THEME.text, whiteSpace: "nowrap" }}>{info.emoji} {info.label}</td>
                    <td style={{ padding: "7px 10px", color: THEME.textMuted, whiteSpace: "nowrap" }}>{l.action}</td>
                    <td style={{ padding: "7px 10px", color: THEME.text }}>
                      {nomObjet(l.identifiant)}
                      <span style={{ color: THEME.textDim, fontSize: 10, marginLeft: 6 }}>{l.identifiant}</span>
                    </td>
                    <td style={{ padding: "7px 10px", color: l.xp > 0 ? THEME.text : THEME.amber, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {nombre(l.xp, 3)}
                    </td>
                    <td style={{ padding: "7px 10px", color: l.argent > 0 ? THEME.emerald : THEME.textDim, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {argent(l.argent)}
                    </td>
                    <td style={{ padding: "7px 10px", color: THEME.textMuted, whiteSpace: "nowrap" }}>
                      {l.niveau_mini > 0 || l.niveau_maxi !== null
                        ? `${l.niveau_mini} → ${l.niveau_maxi === null ? "∞" : l.niveau_maxi}`
                        : "tous"}
                    </td>
                    <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                      {l.actif ? <span style={{ color: THEME.emerald, fontSize: 11 }}>active</span>
                               : <span style={{ color: THEME.textDim, fontSize: 11 }}>inactive</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data.pages > 1 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            style={{ ...champ, cursor: page <= 1 ? "not-allowed" : "pointer", opacity: page <= 1 ? 0.4 : 1 }}>← Précédent</button>
          <span style={{ color: THEME.textMuted, fontSize: 12 }}>page {data.page} sur {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}
            style={{ ...champ, cursor: page >= data.pages ? "not-allowed" : "pointer", opacity: page >= data.pages ? 0.4 : 1 }}>Suivant →</button>
        </div>
      )}

      {/* ── L'avertissement qui a coûté un facteur cinq ───────────── */}
      <div style={{ color: THEME.textDim, fontSize: 10, lineHeight: 1.7, borderTop: `1px solid ${THEME.border}`, paddingTop: 12 }}>
        <strong style={{ color: THEME.amber }}>⚠ Les bornes XP affichées par métier sont un minimum
        et un maximum, pas une moyenne de joueur.</strong> Le 9 août, la moyenne arithmétique des
        47 lignes du Bâtisseur donnait 6,45 XP ; pondérée par la fréquence réelle des actions, la
        vraie valeur était 1,24 — <strong>un facteur cinq</strong>, et 160 heures annoncées au lieu
        de 826 pour atteindre le niveau 100. La moyenne d'un barème n'est pas l'XP moyenne d'un
        joueur, et aucun chiffre de cet écran ne peut la remplacer.
        <br />
        La vérité du barème vit <strong>en base</strong>, pas dans <code>bareme-amorce.yml</code> —
        ce fichier n'est lu qu'au tout premier démarrage, et le modifier ensuite ne change rien.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  LES RÉCOMPENSES DE PALIER
// ═══════════════════════════════════════════════════════════════════════
const NATURES = {
  ARGENT:     { label: "Argent",     emoji: "💰", color: "#3DDC84" },
  OBJET:      { label: "Objet",      emoji: "📦", color: "#4F8EF7" },
  PERMISSION: { label: "Permission", emoji: "🔑", color: "#C9A84C" },
  COSMETIQUE: { label: "Cosmétique", emoji: "✨", color: "#BC8CFF" },
  ACCES:      { label: "Accès",      emoji: "🚪", color: "#E3B341" },
  PASSIF:     { label: "Passif",     emoji: "🌀", color: "#7D8590" },
};
const natureInfo = (n) => NATURES[n] || { label: n, emoji: "•", color: "#7D8590" };

function Recompenses({ token }) {
  const [metier, setMetier] = useState("bucheron");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/catalogue/recompenses?metier=${metier}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, metier]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ color: THEME.textMuted }}>Chargement du catalogue...</div>;
  if (data?.unavailable) return <Indisponible />;
  const err = erreurDe(data);
  if (err) return <Erreur texte={err} />;

  const grille = (data.grille || [])[0];
  const remplis = grille ? grille.niveaux.filter((n) => n.lignes > 0).length : 0;
  const naturesVues = new Set();
  if (grille) grille.niveaux.forEach((n) => n.natures.forEach((x) => naturesVues.add(x)));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select style={champ} value={metier} onChange={(e) => setMetier(e.target.value)}>
          {METIERS.map((m) => <option key={m.key} value={m.key}>{m.emoji} {m.label}</option>)}
        </select>
        <span style={{ color: THEME.textMuted, fontSize: 12 }}>
          <strong style={{ color: remplis === 100 ? THEME.emerald : THEME.amber }}>{remplis}</strong> / 100 paliers renseignés
          {data.somme && <> · {nombre(data.somme.lignes)} lignes · {argent(data.somme.montant)} au total</>}
          {data.somme && data.somme.remises > 0 && <> · {nombre(data.somme.remises)} déjà remises</>}
        </span>
      </div>

      {/* ── La grille des 100 niveaux ─────────────────────────────── */}
      {/* Une liste de ce qui existe ne montre jamais ce qui manque.  */}
      {/* Cent cases, si.                                             */}
      {grille && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(20, 1fr)", gap: 3, maxWidth: 720 }}>
            {grille.niveaux.map((n) => {
              const vide = n.lignes === 0;
              const principale = n.natures[0];
              return (
                <div key={n.niveau}
                  title={vide
                    ? `Niveau ${n.niveau} — rien de prévu`
                    : `Niveau ${n.niveau} — ${n.natures.map((x) => natureInfo(x).label).join(", ")}${n.montant > 0 ? " · " + argent(n.montant) : ""}${n.remises > 0 ? ` · remis ${n.remises} fois` : ""}`}
                  style={{
                    aspectRatio: "1", borderRadius: 3, fontSize: 8,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: vide ? THEME.surface : (natureInfo(principale).color + "33"),
                    border: `1px solid ${vide ? THEME.border : natureInfo(principale).color}`,
                    color: vide ? THEME.textDim : THEME.text,
                    fontWeight: n.niveau % 25 === 0 ? 700 : 400,
                  }}>
                  {n.niveau % 5 === 0 ? n.niveau : ""}
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
            {[...naturesVues].map((n) => {
              const i = natureInfo(n);
              return (
                <span key={n} style={{ color: i.color, fontSize: 10 }}>
                  <span style={{ display: "inline-block", width: 9, height: 9, background: i.color + "33", border: `1px solid ${i.color}`, borderRadius: 2, marginRight: 4 }} />
                  {i.emoji} {i.label}
                </span>
              );
            })}
            <span style={{ color: THEME.textDim, fontSize: 10 }}>
              <span style={{ display: "inline-block", width: 9, height: 9, background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 2, marginRight: 4 }} />
              rien de prévu
            </span>
          </div>
        </div>
      )}

      {/* ── Ce qu'il reste à traduire ─────────────────────────────── */}
      {naturesVues.size <= 1 && remplis > 0 && (
        <div style={{ color: THEME.amber, background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "10px 14px", fontSize: 12, lineHeight: 1.6 }}>
          <strong>Seul l'argent est renseigné pour ce métier.</strong> C'est attendu : l'amorce ne
          pose que la colonne « argent » du classeur, la seule exploitable telle quelle. Les trois
          autres colonnes — objets, avantages, fioles — sont <strong>241 cellules de texte libre en
          français</strong> qu'il faut traduire une par une en identifiants (§ 4.7 du CDC).
          <br />
          Les poser plus tard ne perd rien : le plugin rattrape à la connexion, du niveau 1 au
          niveau atteint. <strong>La seule chose qui casse ce rattrapage est le prestige</strong>,
          qui remet le niveau à 1 — il doit rester éteint tant que le catalogue est incomplet.
        </div>
      )}

      {/* ── Le détail ─────────────────────────────────────────────── */}
      {data.lignes.length === 0 ? (
        <div style={{ color: THEME.textMuted, background: THEME.surface, borderRadius: 8, padding: 20, textAlign: "center", fontSize: 13 }}>
          Aucune récompense n'est prévue pour ce métier.
        </div>
      ) : (
        <div style={{ overflowX: "auto", maxHeight: 460, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 600 }}>
            <thead>
              <tr style={{ color: THEME.textMuted, textAlign: "left" }}>
                {["Niveau", "Nature", "Ce qui est dû", "Quantité", "Montant", "État"].map((h) => (
                  <th key={h} style={{ padding: "6px 10px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", borderBottom: `1px solid ${THEME.border}`, position: "sticky", top: 0, background: THEME.panel, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.lignes.map((l) => {
                const n = natureInfo(l.nature);
                return (
                  <tr key={l.id} style={{ borderBottom: `1px solid ${THEME.border}`, opacity: l.actif ? 1 : 0.5 }}>
                    <td style={{ padding: "6px 10px", color: THEME.text, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{l.niveau}</td>
                    <td style={{ padding: "6px 10px", color: n.color, whiteSpace: "nowrap" }}>{n.emoji} {n.label}</td>
                    <td style={{ padding: "6px 10px", color: THEME.text }}>
                      {l.libelle || (l.identifiant ? nomObjet(l.identifiant) : (l.nature === "ARGENT" ? "—" : "non renseigné"))}
                    </td>
                    <td style={{ padding: "6px 10px", color: THEME.textMuted, textAlign: "right" }}>{l.quantite > 1 ? nombre(l.quantite) : ""}</td>
                    <td style={{ padding: "6px 10px", color: l.montant > 0 ? THEME.emerald : THEME.textDim, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {l.montant > 0 ? argent(l.montant) : "—"}
                    </td>
                    <td style={{ padding: "6px 10px" }}>
                      {l.actif ? <span style={{ color: THEME.emerald, fontSize: 11 }}>active</span>
                               : <span style={{ color: THEME.textDim, fontSize: 11 }}>inactive</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ color: THEME.textDim, fontSize: 10, lineHeight: 1.7, borderTop: `1px solid ${THEME.border}`, paddingTop: 12 }}>
        Ce tableau est le <strong>catalogue</strong> : ce qui est dû. Il se corrige et se complète.
        Le <strong>registre</strong> de ce qui a été remis est ailleurs (<code>oj_recompense_donnee</code>),
        et lui ne bouge jamais — c'est ce partage qui permet de compléter le catalogue sans redonner
        à tout le monde tout ce qu'il contient.
      </div>
    </div>
  );
}
